
import helpers from './helpers'

/**
* Helper functions for working with Prismic documents.
*/
export default {
  /**
  * Checks a prismic document to see if the user has access to it.
  *
  * @param doc {object} - Prismic document
  * @returns {boolean}
  */
  userHasPermission(doc) {
    const platinumRequirement = doc.platinumPermission ? doc.platinumPermission : 'both'


    const plusRequirement = doc.plusPermission ? doc.plusPermission : 'both'


    const loginRequirement = doc.loginPermission ? doc.loginPermission : 'both'

    if (!window.__USER__) {
      return false
    }

    // Check logged in
    if (loginRequirement !== 'both') {
      const notLoggedIn = loginRequirement.indexOf('not') > -1

      if ((window.__USER__.loggedIn && notLoggedIn) || (!window.__USER__.loggedIn && !notLoggedIn)) {
        return false
      }
    }

    // Check Bookshelf PLUS
    if (plusRequirement !== 'both') {
      const notPlus = plusRequirement.indexOf('not') > -1

      if ((window.__USER__.plus && notPlus) || (!window.__USER__.plus && !notPlus)) {
        return false
      }
    }

    // Check Platinum
    if (platinumRequirement !== 'both') {
      const notPlatinum = platinumRequirement.indexOf('not') > -1

      if ((window.__USER__.platinum && notPlatinum) || (!window.__USER__.platinum && !notPlatinum)) {
        return false
      }
    }

    return true
  },

  /**
  * formats the main image and image views into a valid `srcset` string.
  *
  * @param image {object} - Prismic image object
  */
  formatSrcset(image) {
    let srcset = ''

    if (image.views) {
      for (const key in image.views) {
        if (image.views.hasOwnProperty(key)) {
          srcset += `${image.views[key].url} ${image.views[key].width}w, `
        }
      }
    }

    return `${srcset + image.url} ${image.width}w`
  },

  /**
  * Returns a string of helper classes for when the given document should be hidden.
  * @returns {string}
  */
  formatViewClasses(doc) {
    const classes = []

    if (!doc) {
      return ''
    }

    if (!this.visibleOnMobile(doc)) {
      classes.push('hidden-xs')
    }

    if (!this.visibleOnTablet(doc)) {
      classes.push('hidden-sm')
    }

    if (!this.visibleOnDesktop(doc)) {
      classes.push('hidden-md hidden-lg')
    }

    return classes.join(' ')
  },

  /**
  * Checks if the document should be visible on mobile sized screens.
  * @returns {boolean}
  */
  visibleOnMobile(doc) {
    if (doc && doc.showMobile && doc.showMobile === 'no') {
      return false
    }

    return true
  },

  /**
  * Checks if the document should be visible on tablet sized screens.
  * @returns {boolean}
  */
  visibleOnTablet(doc) {
    if (doc && doc.showTablet && doc.showTablet === 'no') {
      return false
    }

    return true
  },

  /**
  * Checks if the document should be visible on desktop sized screens.
  * @returns {boolean}
  */
  visibleOnDesktop(doc) {
    if (doc && doc.showDesktop && doc.showDesktop === 'no') {
      return false
    }

    return true
  },

  /**
  * Checks to see if the document would be visible based on current viewport size.
  * @returns {boolean}
  */
  currentlyVisible(doc) {
    if (!this.userHasPermission(doc)) {
      return false
    }
    const viewportSize = helpers.getScreenWidth()

    if (viewportSize < 768) {
      return this.visibleOnMobile(doc)
    } else if (viewportSize < 992) {
      return this.visibleOnTablet(doc)
    }

    return this.visibleOnDesktop(doc)
  },

  /**
  * Returns a formatted spacing class name based on the specified `attr` in the
  * given `doc`.
  *
  * @param doc {object} Prismic JSON
  * @param attr {string} attribute where spacing info is stored.
  * @returns {string}
  */
  getSpacingClass(doc, attr) {
    if (!doc || !attr) {
      return ''
    }
    // Modify spacing class as needed.
    let value = doc[attr] || 'default'

    if (value === 'none') {
      value = 'no'
    }

    switch (attr) {
      case 'verticalMargin':
        return `has-${value}-vertical-margin`
      case 'horizontalMargin':
        return `has-${value}-horizontal-margin`
      case 'bannerSpacing':
        return `has-${value}-banner-spacing`
      default:
        return ''
    }
  },
}
